import { createRouter, createWebHistory } from 'vue-router'
import { getTokens, isSignedIn } from '@dacr/auth'
import { decodeJWT } from '@/shared/utils/jwt'
import { UserPermission, type User } from '@/shared/graphql/legacy/generated'
import { useWebviewStore } from '@/shared/stores/webview'
import { useBillingStore } from '@/features/billing/store'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { authGuard, featureFlagGuard } from './guards'
import { setupMiddleware } from './middleware'

// Auth
import LoginView from '@/features/auth/views/LoginView.vue'
import OAuthAuthorizeView from '@/features/auth/views/OAuthAuthorizeView.vue'
import RegisterView from '@/features/auth/views/RegisterView.vue'
import RegisterFormView from '@/features/auth/views/RegisterFormView.vue'
import RegisterOrganizationView from '@/features/auth/views/RegisterOrganizationView.vue'
import RegisterVerifyIdentityView from '@/features/auth/views/RegisterVerifyIdentityView.vue'
import AuthView from '@/features/auth/views/AuthView.vue'
import AuthSuccessView from '@/features/auth/views/AuthSuccessView.vue'
import ForgotPasswordView from '@/features/auth/views/ForgotPasswordView.vue'
import ResetPasswordView from '@/features/auth/views/ResetPasswordView.vue'
import SignoutView from '@/features/auth/views/SignoutView.vue'

// Onboarding
import OnboardingView from '@/features/onboarding/views/OnboardingView.vue'
import OnboardingStepsView from '@/features/onboarding/views/OnboardingStepsView.vue'
import OnboardingSelectPlanView from '@/features/onboarding/views/OnboardingSelectPlanView.vue'

// Billing
import CheckoutView from '@/features/billing/views/CheckoutView.vue'
import UpgradeView from '@/features/billing/views/UpgradeView.vue'
import UpgradeSelectPlanView from '@/features/billing/views/UpgradeSelectPlanView.vue'
import ChangePlansView from '@/features/billing/views/ChangePlansView.vue'
import ChangePlansSelectPlanView from '@/features/billing/views/ChangePlansSelectPlanView.vue'
import ChangePlansConfirmView from '@/features/billing/views/ChangePlansConfirmView.vue'

// Explorer
import FileExplorerView from '@/features/explorer/views/FileExplorerView.vue'
import FileExplorerFolderView from '@/features/explorer/views/FileExplorerFolderView.vue'
import ArchiveView from '@/features/explorer/views/ArchiveView.vue'

// Settings
import SettingsView from '@/features/settings/views/SettingsView.vue'
import SettingsAccountView from '@/features/settings/views/SettingsAccountView.vue'
import SettingsBillingView from '@/features/settings/views/SettingsBillingView.vue'
import SettingsSecurityView from '@/features/settings/views/SettingsSecurityView.vue'

// Copyright
import CopyrightView from '@/features/copyright/views/CopyrightView.vue'
import TranscriptView from '@/features/copyright/views/TranscriptView.vue'
import SheetMusicView from '@/features/copyright/views/SheetMusicView.vue'

// Infringement
import InfringementView from '@/features/infringement/views/InfringementView.vue'
import InfringementListView from '@/features/infringement/views/InfringementListView.vue'
import InfringementPreviewView from '@/features/infringement/views/InfringementPreviewView.vue'
import InfringementReportView from '@/features/infringement/views/InfringementReportView.vue'
import InfringementReportMatchesView from '@/features/infringement/views/InfringementReportMatchesView.vue'

// Search
import FileSearchResultsView from '@/features/search/views/FileSearchResultsView.vue'
import PublicSearchResultsView from '@/features/search/views/PublicSearchResultsView.vue'

// Misc
import NotFoundView from '@/shared/components/NotFoundView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/home',
    },
    {
      path: '/login',
      name: 'Login',
      component: LoginView,
      meta: { layout: 'default' },
      beforeEnter(_, __, next) {
        if (isSignedIn()) {
          return next('/home')
        }

        next()
      },
    },
    {
      path: '/oauth/authorize',
      name: 'OAuthAuthorize',
      component: OAuthAuthorizeView,
      meta: { layout: 'default' },
    },
    {
      path: '/register',
      component: RegisterView,
      beforeEnter(to, __, next) {
        if (isSignedIn() && !to.query.ott) {
          next('/home')
        }

        if (isSignedIn() && to.query.ott) {
          next({
            path: '/auth',
            query: {
              ott: to.query.ott,
            },
          })
        }

        if (to.name === 'RegisterRootView') {
          next()
        } else {
          next('/register')
        }
      },
      children: [
        {
          path: '',
          name: 'RegisterRootView',
          component: RegisterFormView,
        },
        {
          path: 'organization',
          name: 'RegisterOrganizationView',
          component: RegisterOrganizationView,
        },
        {
          path: 'identity',
          name: 'RegisterVerifyIdentityView',
          component: RegisterVerifyIdentityView,
          beforeEnter: [featureFlagGuard(FeatureFlag.Origin)],
        },
      ],
    },
    {
      path: '/auth',
      meta: { layout: 'default' },
      beforeEnter: (to, _, next) => {
        if (!isSignedIn()) {
          return next({
            path: '/login',
            query: {
              ott: to.query.ott,
            },
          })
        }

        next()
      },
      children: [
        {
          path: '',
          name: 'AuthView',
          component: AuthView,
          beforeEnter: (to, _, next) => {
            if (!to.query.ott) {
              return next('/home')
            }

            next()
          },
        },
        {
          path: 'success',
          name: 'AuthSuccessView',
          component: AuthSuccessView,
          beforeEnter: (_, from, next) => {
            if (
              !from.path.includes('login') &&
              !from.path.includes('register') &&
              !from.path.includes('auth')
            ) {
              return next('/home')
            }

            next()
          },
        },
      ],
    },
    {
      path: '/onboarding',
      name: 'Onboarding',
      component: OnboardingView,
      beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'OnboardingSteps',
          component: OnboardingStepsView,
        },
        {
          path: 'pro',
          name: 'OnboardingPro',
          component: OnboardingSelectPlanView,
        },
        {
          path: 'checkout',
          name: 'OnboardingCheckout',
          component: CheckoutView,
          beforeEnter: (_, __, next) => {
            const planStore = useBillingStore()

            if (planStore.selectedPlan) {
              return next()
            }

            next('/')
          },
        },
      ],
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: ForgotPasswordView,
      meta: { layout: 'default' },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPasswordView,
      meta: { layout: 'default' },
    },
    {
      path: '/upgrade',
      beforeEnter: [
        (_, __, next) => {
          const webviewStore = useWebviewStore()

          if (webviewStore.isActive) {
            return next()
          }

          if (!isSignedIn()) {
            return next('/login')
          }

          const userData = localStorage.getItem('dacrUser')

          if (!userData) {
            return next('/home')
          }

          const user = JSON.parse(userData || '{}') as User

          return !user.permissions.includes(UserPermission.Pro)
            ? next()
            : next('/home')
        },
        (to, _, next) => {
          if (to.name === 'UpgradeCheckoutView') {
            next('/upgrade')
          } else {
            next()
          }
        },
      ],
      component: UpgradeView,
      children: [
        {
          path: '',
          name: 'UpgradeSelectPlanView',
          component: UpgradeSelectPlanView,
        },
        {
          path: 'checkout',
          name: 'UpgradeCheckoutView',
          component: CheckoutView,
          beforeEnter: (_, __, next) => {
            const planStore = useBillingStore()

            if (planStore.selectedPlan) {
              return next()
            }

            next('/')
          },
        },
      ],
    },
    {
      path: '/change-plans',
      name: 'ChangePlansView',
      component: ChangePlansView,
      beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'ChangePlansSelectPlanView',
          component: ChangePlansSelectPlanView,
        },
        {
          path: 'confirm',
          name: 'ChangePlansConfirmView',
          component: ChangePlansConfirmView,
        },
        {
          path: 'checkout',
          name: 'ChangePlansCheckoutView',
          component: CheckoutView,
          beforeEnter: (_, __, next) => {
            const planStore = useBillingStore()

            if (planStore.selectedPlan) {
              return next()
            }

            next('/')
          },
        },
      ],
    },
    {
      path: '/home',
      component: FileExplorerView,
      meta: { layout: 'shell', sidebar: true },
      beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'FileExplorerRootFolderView',
          component: FileExplorerFolderView,
        },
        {
          path: ':path+',
          name: 'FileExplorerFolderView',
          component: FileExplorerFolderView,
        },
      ],
    },
    {
      path: '/archive',
      component: ArchiveView,
      meta: { layout: 'shell' },
      beforeEnter: authGuard,
    },
    {
      path: '/settings',
      component: SettingsView,
      meta: { layout: 'default' },
      beforeEnter(_, __, next) {
        const webviewStore = useWebviewStore()

        if (webviewStore.isActive) {
          const { accessToken } = getTokens()

          if (!accessToken || !decodeJWT(accessToken)) {
            return next('/404')
          }

          return next()
        }

        if (!isSignedIn()) {
          return next('/login')
        }

        next()
      },
      children: [
        {
          path: '',
          name: 'SettingsView',
          redirect: '/settings/account',
        },
        {
          path: 'account',
          name: 'SettingsAccountView',
          component: SettingsAccountView,
        },
        {
          path: 'billing',
          name: 'SettingsBillingView',
          component: SettingsBillingView,
        },
        {
          path: 'security',
          name: 'SettingsSecurityView',
          component: SettingsSecurityView,
        },
      ],
    },
    {
      path: '/file/:id',
      meta: { layout: 'default' },
      children: [
        {
          path: '',
          component: CopyrightView,
          name: 'CopyrightView',
        },
        {
          path: 'transcript',
          component: TranscriptView,
          name: 'TranscriptView',
        },
        {
          path: 'tracks/:trackIndex/transcript',
          component: TranscriptView,
          name: 'TrackTranscriptView',
        },
        {
          path: 'tracks/:trackIndex/:stem/sheet-music',
          component: SheetMusicView,
          name: 'TrackSheetMusicView',
        },
      ],
    },
    {
      path: '/infringement',
      name: 'InfringementView',
      component: InfringementView,
      meta: { layout: 'shell' },
      beforeEnter: [authGuard, featureFlagGuard(FeatureFlag.AI)],
      children: [
        {
          path: '',
          component: InfringementListView,
          name: 'InfringementListView',
        },
        {
          path: 'group/:groupId/:timestamp',
          component: InfringementListView,
          name: 'InfringementGroupView',
        },
      ],
    },
    {
      path: '/infringement/preview/:id',
      name: 'InfringementPreviewView',
      component: InfringementPreviewView,
      meta: { layout: 'default' },
      beforeEnter: [authGuard, featureFlagGuard(FeatureFlag.AI)],
    },
    {
      path: '/infringement/report/:id',
      name: 'InfringementReportView',
      component: InfringementReportView,
      meta: { layout: 'default' },
      beforeEnter: [featureFlagGuard(FeatureFlag.AI)],
    },
    {
      path: '/infringement/report/:id/:category(image|text|transcript)',
      name: 'InfringementReportMatchesView',
      component: InfringementReportMatchesView,
      meta: { layout: 'default' },
      beforeEnter: [featureFlagGuard(FeatureFlag.AI)],
    },
    {
      path: '/search',
      beforeEnter: authGuard,
      children: [
        {
          path: 'files',
          component: FileSearchResultsView,
          meta: { layout: 'shell' },
        },
        {
          path: 'public',
          component: PublicSearchResultsView,
          meta: { layout: 'default' },
        },
      ],
    },
    {
      path: '/checkout',
      component: CheckoutView,
      meta: { layout: 'default' },
      beforeEnter: authGuard,
    },
    {
      path: '/signout',
      component: SignoutView,
      beforeEnter: authGuard,
    },
    {
      path: '/:notFound(.*)*',
      component: NotFoundView,
      meta: { layout: 'default' },
    },
  ],
})

setupMiddleware(router)

export default router
